import { z } from 'zod';

import {
  COACH_IDS,
  TOUR_IDS,
  type CoachDefinition,
  type CoachId,
  type TourDefinition,
} from '../types';

/**
 * CONTRACT §5 — browser-local guide progress.
 *
 * `{ tours: { [TourId]: { v, status, step? } }, coaches?: { [CoachId]: { v,
 * status, step?, active? } }, invitation? }`, validated with zod on every read.
 * Anything invalid, tampered with or written for another version of a guide is
 * ignored, never trusted; a storage that throws (private mode, quota, a blocked
 * origin) degrades to memory for the session.
 *
 * Only guide facts are stored: a guide ID, its version, a status and a step ID
 * — and, for a coach, `active` while it is running, so that after a reload it
 * can offer "Continue?" (it never acts by itself). Never a record ID, entity
 * content, a document URL or a token: the record a coach is following lives in
 * memory only. "Completed" means a guide was finished — not audit, acceptance
 * or business completion.
 *
 * The `coaches` entry is ADDITIVE: progress written before coaches existed
 * reads exactly as it did.
 */
const STEP_ID = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/);

const STATUS = z.enum(['in_progress', 'completed', 'dismissed']);

const ENTRY = z
  .object({
    v: z.number().int().min(1).max(10_000),
    status: STATUS,
    step: STEP_ID.optional(),
  })
  .strict();

const COACH_ENTRY = z
  .object({
    v: z.number().int().min(1).max(10_000),
    status: STATUS,
    step: STEP_ID.optional(),
    /** Running when the page was left (a reload, a sign-out, an expired session). */
    active: z.literal(true).optional(),
  })
  .strict();

const PROGRESS = z
  .object({
    tours: z.partialRecord(z.enum(TOUR_IDS), ENTRY),
    coaches: z.partialRecord(z.enum(COACH_IDS), COACH_ENTRY).optional(),
    invitation: z.literal('dismissed').optional(),
  })
  .strict();

export type ProgressData = z.infer<typeof PROGRESS>;
export type TourStatus = 'not_started' | 'in_progress' | 'completed';
export type CoachProgressStatus = 'in_progress' | 'completed' | 'dismissed';

const EMPTY: ProgressData = { tours: {} };

/** Parses stored text; anything that is not exactly our shape reads as nothing. */
export function parseProgress(raw: string | null): ProgressData {
  if (raw === null) return EMPTY;

  try {
    const parsed = PROGRESS.safeParse(JSON.parse(raw));

    return parsed.success ? parsed.data : EMPTY;
  } catch {
    return EMPTY;
  }
}

export interface ProgressStore {
  get: () => ProgressData;
  subscribe: (listener: () => void) => () => void;
  /** The status for THIS version of the tour; another version's entry reads as not started. */
  status: (def: TourDefinition) => TourStatus;
  /** Where an unfinished guide stopped — a step ID, to be rechecked against what is visible now. */
  resumeStep: (def: TourDefinition) => string | undefined;
  setTour: (def: TourDefinition, status: 'in_progress' | 'completed', step?: string) => void;
  /** A coach's status, read as a tour's: dismissed, or another version, is not started. */
  coachStatus: (def: CoachDefinition) => TourStatus;
  /** Where an unfinished coach stopped. */
  coachResumeStep: (def: CoachDefinition) => string | undefined;
  /** Whether this coach was running when the page was left (offer "Continue?"). */
  coachActive: (def: CoachDefinition) => boolean;
  /**
   * Records a coach's progress. `active` is kept while it runs and dropped when
   * it is paused, closed or finished.
   */
  setCoach: (
    def: CoachDefinition,
    status: CoachProgressStatus,
    options?: { step?: string; active?: boolean },
  ) => void;
  dismissInvitation: () => void;
  /** True when writes cannot reach browser storage (no key yet, or storage refused). */
  isMemoryOnly: () => boolean;
}

function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function createProgressStore(
  key: string | null,
  storage: Storage | null = browserStorage(),
): ProgressStore {
  const target = key !== null && storage !== null ? { key, storage } : null;
  let memoryOnly = target === null;
  let data: ProgressData = EMPTY;
  const listeners = new Set<() => void>();

  if (target !== null) {
    try {
      data = parseProgress(target.storage.getItem(target.key));
    } catch {
      memoryOnly = true;
    }
  }

  const write = (next: ProgressData) => {
    data = next;

    if (target !== null && !memoryOnly) {
      try {
        target.storage.setItem(target.key, JSON.stringify(next));
      } catch {
        memoryOnly = true;
      }
    }

    for (const listener of listeners) listener();
  };

  const entryFor = (def: TourDefinition) => {
    const entry = data.tours[def.id];

    return entry !== undefined && entry.v === def.version ? entry : undefined;
  };

  const coachEntryFor = (def: CoachDefinition) => {
    const entry = data.coaches?.[def.id];

    return entry !== undefined && entry.v === def.version ? entry : undefined;
  };

  const storable = (step: string | undefined) =>
    step !== undefined && STEP_ID.safeParse(step).success ? step : undefined;

  return {
    get: () => data,
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    status: (def) => {
      const entry = entryFor(def);

      if (entry === undefined || entry.status === 'dismissed') return 'not_started';

      return entry.status;
    },
    resumeStep: (def) => {
      const entry = entryFor(def);

      return entry?.status === 'in_progress' ? entry.step : undefined;
    },
    setTour: (def, status, step) => {
      const entry =
        step !== undefined && STEP_ID.safeParse(step).success
          ? { v: def.version, status, step }
          : { v: def.version, status };

      write({ ...data, tours: { ...data.tours, [def.id]: entry } });
    },
    coachStatus: (def) => {
      const entry = coachEntryFor(def);

      if (entry === undefined || entry.status === 'dismissed') return 'not_started';

      return entry.status;
    },
    coachResumeStep: (def) => {
      const entry = coachEntryFor(def);

      return entry?.status === 'in_progress' ? entry.step : undefined;
    },
    coachActive: (def) => {
      const entry = coachEntryFor(def);

      return entry?.status === 'in_progress' && entry.active === true;
    },
    setCoach: (def, status, options = {}) => {
      const step = storable(options.step);
      const entry: NonNullable<ProgressData['coaches']>[CoachId] = {
        v: def.version,
        status,
        ...(step === undefined ? {} : { step }),
        ...(options.active === true && status === 'in_progress' ? { active: true as const } : {}),
      };

      write({ ...data, coaches: { ...data.coaches, [def.id]: entry } });
    },
    dismissInvitation: () => write({ ...data, invitation: 'dismissed' }),
    isMemoryOnly: () => memoryOnly,
  };
}
